(() => {
  const APP_SELECTOR = '#noise-test-app';

  function initializeAudioTests(container = document) {
    const apps = container.querySelectorAll(APP_SELECTOR);
    apps.forEach(initAudioTestApp);
  }

  // Inicialização normal da página
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAudioTests();
    });
  } else {
    initializeAudioTests();
  }

  // Reinicialização quando a Shopify atualiza uma secção
  document.addEventListener('shopify:section:load', (event) => {
    initializeAudioTests(event.target);
  });

  // Atualização ao adicionar, remover ou selecionar blocos
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[id^="shopify-section-"]');

    if (section) {
      initializeAudioTests(section);
    }
  });

  function initAudioTestApp(app) {
    if (!app || app.dataset.initialized === 'true') return;
    app.dataset.initialized = 'true';

    const sectionId = app.getAttribute('data-section');
    const config =
      (window.AudioTestConfig && window.AudioTestConfig[sectionId]) || {};

    const stage = app.querySelector('#noise-test-stage');
    if (!stage) return;

    const state = {
      step: 'ready',
      measurementDuration: Number(config.measurementDuration || 5),
      noiseScore: 0,
      displayDb: 0,
      audioStream: null,
      audioContext: null,
      analyser: null,
      rafId: null,
      measureStartTime: 0,
      measureSamples: [],
      isMeasuring: false
    };

    renderReadyScreen();

    
    function renderReadyButtons() {
      const buttons = [];

      // Botão principal: Iniciar medição
      const mainLabel =
        config.readyButtonLabel || 'Iniciar medição';

      const mainStyle =
        config.readyButtonStyle === 'secondary'
          ? 'noise-test-btn--secondary'
          : '';

      const mainUrl = config.readyButtonLink || '';

      if (mainUrl) {
        buttons.push(`
          <a
            class="noise-test-btn ${mainStyle}"
            href="${escapeAttribute(mainUrl)}"
          >
            ${escapeHtml(mainLabel)}
          </a>
        `);
      } else {
        buttons.push(`
          <button
            class="noise-test-btn ${mainStyle}"
            type="button"
            data-action="start-measurement"
          >
            ${escapeHtml(mainLabel)}
          </button>
        `);
      }

      // Botões adicionais configurados na Shopify
      const extraButtons = Array.isArray(config.extraButtons)
        ? config.extraButtons
        : [];

      extraButtons.forEach((button) => {
        if (!button || !button.label || !button.url) return;

        const style =
          button.style === 'secondary'
            ? 'noise-test-btn--secondary'
            : '';

        buttons.push(`
          <a
            class="noise-test-btn ${style}"
            href="${escapeAttribute(button.url)}"
          >
            ${escapeHtml(button.label)}
          </a>
        `);
      });

      return buttons.join('');
    }


    function renderReadyScreen() {
      state.step = 'ready';

      stage.innerHTML = `
        <div class="noise-test-screen">
          <div class="noise-test-center">
            <div class="noise-test-center-inner">
              <h2 class="noise-test-center-title">${escapeHtml(
                config.readyTitle || 'Vamos medir o ruído à sua volta'
              )}</h2>
              <p class="noise-test-center-copy">${escapeHtml(
                config.readyText ||
                'No próximo passo, o browser vai pedir acesso ao microfone.'
              )}</p>

              <div class="noise-test-btn-row" style="justify-content:center;">
                ${renderReadyButtons()}
              </div>

              <div class="noise-test-disclaimer">
                O som é processado apenas no seu navegador e não é gravado.
              </div>
            </div>
          </div>
        </div>
      `;

      bindCommonButtons();
    }

    function renderMeasuringScreen() {
      state.step = 'measuring';

      stage.innerHTML = `
        <div class="noise-test-screen">
          <div class="noise-test-center">
            <div class="noise-test-center-inner">
              <h2 class="noise-test-center-title">${escapeHtml(
                config.measuringTitle || 'A medir o ruído do ambiente'
              )}</h2>
              <p class="noise-test-center-copy">${escapeHtml(
                config.measuringText ||
                'Aguarde alguns segundos enquanto o som ambiente é analisado.'
              )}</p>

              <div class="noise-test-wave" aria-hidden="true">
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
                <span class="noise-test-wave-bar"></span>
              </div>

              <div class="noise-test-disclaimer" id="noise-test-live-status">
                A medir durante ${state.measurementDuration} segundos...
              </div>
            </div>
          </div>
        </div>
      `;
    }

    function renderResultScreen() {
      state.step = 'result';

      const result = getNoiseInterpretation(state.noiseScore);

      const centerX = 160;
      const centerY = 160;
      const radius = 110;

      const startAngle = 210;
      const endAngle = 510;
      const totalSweep = endAngle - startAngle;

      const normalizedScore = Math.max(0, Math.min(100, state.noiseScore));
      const fillEndAngle = startAngle + (totalSweep * normalizedScore) / 100;

      const trackPath = describeArc(centerX, centerY, radius, startAngle, endAngle);
      const fillPath = describeArc(centerX, centerY, radius, startAngle, fillEndAngle);

      stage.innerHTML = `
        <div class="noise-test-screen">
          <div class="noise-test-center">
            <div class="noise-test-center-inner">
              <div class="noise-test-meter-wrap is-${escapeAttribute(result.level)}">
                <svg class="noise-test-meter-svg" viewBox="0 0 320 320" aria-hidden="true">
                  <path class="noise-test-meter-track" d="${trackPath}"></path>
                  <path class="noise-test-meter-fill" d="${fillPath}"></path>
                </svg>

                <div class="noise-test-meter-content">
                  <div class="noise-test-meter-badge">
                    <span class="noise-test-meter-dot">${result.level === 'high' ? '!' : '✓'}</span>
                    <span>${escapeHtml(result.badge)}</span>
                  </div>

                  <p class="noise-test-meter-number">${formatDb(state.displayDb)}</p>
                </div>
              </div>

              <h2 class="noise-test-result-title">${escapeHtml(result.title)}</h2>
              <h5 class="noise-test-result-subtitle">${escapeHtml(result.subtitle)}</h5>
              <p class="noise-test-result-text">${escapeHtml(result.text)}</p>

              <div class="noise-test-disclaimer">
                ${escapeHtml(
                  config.disclaimerText ||
                  'Esta medição é indicativa e não substitui avaliação profissional.'
                )}
              </div>

              <div class="noise-test-btn-row" style="justify-content:center;">
                <button class="noise-test-btn noise-test-btn--secondary" type="button" data-action="retry-measurement">
                  ${escapeHtml(config.retryButtonLabel || 'Voltar a medir')}
                </button>
                <button class="noise-test-btn" type="button" data-action="restart-all">
                  ${escapeHtml(config.restartButtonLabel || 'Recomeçar teste')}
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      bindCommonButtons();
    }

    function bindCommonButtons() {
      const continueReady = stage.querySelector('[data-action="continue-ready"]');
      const backIntro = stage.querySelector('[data-action="back-intro"]');
      const startMeasurement = stage.querySelector('[data-action="start-measurement"]');
      const retryMeasurement = stage.querySelector('[data-action="retry-measurement"]');
      const restartAll = stage.querySelector('[data-action="restart-all"]');

      if (continueReady) continueReady.addEventListener('click', renderReadyScreen);
      if (backIntro) backIntro.addEventListener('click', renderIntro);
      if (startMeasurement) startMeasurement.addEventListener('click', beginMeasurementFlow);
      if (retryMeasurement) retryMeasurement.addEventListener('click', beginMeasurementFlow);

      if (restartAll) {
        restartAll.addEventListener('click', () => {
          cleanupAudio();
          state.noiseScore = 0;
          state.displayDb = 0;
          renderReadyScreen();
        });
      }
    }

    async function beginMeasurementFlow() {
      cleanupAudio();
      renderMeasuringScreen();

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showMeasurementError(
          'Este navegador não suporta acesso ao microfone.'
        );
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });

        state.audioStream = stream;
        state.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        state.analyser = state.audioContext.createAnalyser();

        const source = state.audioContext.createMediaStreamSource(stream);
        source.connect(state.analyser);

        state.analyser.fftSize = 2048;
        state.analyser.smoothingTimeConstant = 0.8;

        state.measureSamples = [];
        state.measureStartTime = performance.now();
        state.isMeasuring = true;

        const timeData = new Uint8Array(state.analyser.fftSize);
        const liveStatus = stage.querySelector('#noise-test-live-status');

        const tick = (now) => {
          if (!state.isMeasuring || !state.analyser) return;

          state.analyser.getByteTimeDomainData(timeData);

          let sumSquares = 0;
          for (let i = 0; i < timeData.length; i++) {
            const normalized = (timeData[i] - 128) / 128;
            sumSquares += normalized * normalized;
          }

          const rms = Math.sqrt(sumSquares / timeData.length);
          const relativeScore = convertRmsToRelativeScore(rms);
          state.measureSamples.push(relativeScore);

          const elapsed = (now - state.measureStartTime) / 1000;
          const remaining = Math.max(0, state.measurementDuration - elapsed);

          if (liveStatus) {
            liveStatus.textContent = `A medir... ${remaining.toFixed(1)} s`;
          }

          if (elapsed >= state.measurementDuration) {
            finishMeasurement();
            return;
          }

          state.rafId = requestAnimationFrame(tick);
        };

        state.rafId = requestAnimationFrame(tick);
      } catch (error) {
        cleanupAudio();
        showMeasurementError(getFriendlyError(error));
      }
    }

    function finishMeasurement() {
      state.isMeasuring = false;

      if (!state.measureSamples.length) {
        showMeasurementError('Não foi possível recolher dados suficientes.');
        cleanupAudio();
        return;
      }

      const average =
        state.measureSamples.reduce((acc, value) => acc + value, 0) /
        state.measureSamples.length;

      state.noiseScore = Math.max(0, Math.min(100, Math.round(average)));
      state.displayDb = mapRelativeScoreToEstimatedDb(state.noiseScore);

      cleanupAudio();
      renderResultScreen();
    }

    function cleanupAudio() {
      state.isMeasuring = false;

      if (state.rafId) {
        cancelAnimationFrame(state.rafId);
        state.rafId = null;
      }

      if (state.audioStream) {
        state.audioStream.getTracks().forEach((track) => track.stop());
        state.audioStream = null;
      }

      if (state.audioContext) {
        try {
          state.audioContext.close();
        } catch (e) {}
        state.audioContext = null;
      }

      state.analyser = null;
    }

    function showMeasurementError(message) {
      stage.innerHTML = `
        <div class="noise-test-screen">
          <div class="noise-test-center">
            <div class="noise-test-center-inner">
              <h2 class="noise-test-center-title">Não foi possível usar o microfone</h2>
              <p class="noise-test-center-copy">
                Para medir o ruído ambiente, é necessário dar permissão ao microfone no navegador.
              </p>
              <div class="noise-test-error">${escapeHtml(message)}</div>
              <div class="noise-test-btn-row" style="justify-content:center;">
                <button class="noise-test-btn noise-test-btn--secondary" type="button" data-action="back-ready">
                  Voltar
                </button>
                <button class="noise-test-btn" type="button" data-action="retry-measurement">
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      const backReady = stage.querySelector('[data-action="back-ready"]');
      const retryMeasurement = stage.querySelector('[data-action="retry-measurement"]');

      if (backReady) backReady.addEventListener('click', renderReadyScreen);
      if (retryMeasurement) retryMeasurement.addEventListener('click', beginMeasurementFlow);
    }

    function getNoiseInterpretation(score) {
      if (score < 34) {
        return {
          level: 'low',
          badge: 'Baixo',
          title: config.lowResultTitle || 'Ambiente com pouco ruído!',
          subtitle: config.lowResultSubtitle || 'Sem risco para a audição',
          text: config.lowResultText || 'O ruído à sua volta encontra-se a um nível baixo.'
        };
      }

      if (score < 67) {
        return {
          level: 'medium',
          badge: 'Médio',
          title: config.mediumResultTitle || 'Ambiente com algum ruído!',
          subtitle: config.mediumResultSubtitle || 'Algum risco para a audição',
          text: config.mediumResultText || 'Existe ruído moderado no ambiente.'
        };
      }

      return {
        level: 'high',
        badge: 'Alto',
        title: config.highResultTitle || 'Ambiente com muito ruído!',
        subtitle: config.highResultSubtitle || 'Audição em risco, usar proteção',
        text: config.highResultText || 'O ambiente encontra-se bastante ruidoso.'
      };
    }

    function convertRmsToRelativeScore(rms) {
      const boosted = rms * 580;
      return Math.max(0, Math.min(100, boosted));
    }

    function mapRelativeScoreToEstimatedDb(score) {
      const clamped = Math.max(0, Math.min(100, score));
      return Math.round((16 + (clamped / 100) * 66) * 10) / 10;
    }

    function formatDb(value) {
      const rounded = Math.round(value * 10) / 10;
      return `${rounded}<small>dB</small>`;
    }

    function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians)
      };
    }

    function describeArc(x, y, radius, startAngle, endAngle) {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

      return [
        'M', start.x, start.y,
        'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
      ].join(' ');
    }

    function getFriendlyError(error) {
      if (!error) {
        return 'O acesso ao microfone foi bloqueado ou não está disponível.';
      }

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return 'Permissão recusada. Abra as permissões do navegador e permita o acesso ao microfone.';
      }

      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        return 'Não foi encontrado nenhum microfone neste dispositivo.';
      }

      if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        return 'O microfone já está a ser usado por outra aplicação ou não pôde ser iniciado.';
      }

      if (error.name === 'SecurityError') {
        return 'O acesso ao microfone foi bloqueado por motivos de segurança.';
      }

      return error.message || 'O acesso ao microfone falhou.';
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function escapeAttribute(value) {
      return escapeHtml(value);
    }
  }
})();